import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import { getRadarStatus, getOnlinePlayers } from "@/api/gameApi"
import { ColorRank, OnlinePlayer, RadarSearchMode } from "@/types/battle"
import { useBattleChallenge } from "@/features/battle/BattleChallengeContext"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { RANK_NAMES } from "@/constants/guardians"
import { RADAR_ENERGY_COST } from "@/constants/game.constants"
import {
  connectBattleSocket,
  emitBattleEvent,
  emitBattleEventWhenReady,
  onBattleEvent,
  subscribeBattlePresence,
} from "@/socket/battleSocket"
import { buildRadarSearchDto } from "@/utils/radarStatus"
import { formatPointsValue } from "@/constants/game.constants"
import { DECK_BATTLE_ENABLED } from "@/constants/guardians"

const RANKS: ColorRank[] = ["WHITE", "GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]

export default function BattleLobbyPage() {
  const navigate = useNavigate()
  const { passport } = useGuardiansPassport()
  const { sendChallenge } = useBattleChallenge()
  const [tab, setTab] = useState<"online" | "radar">("online")
  const [onlineCount, setOnlineCount] = useState<number | null>(null)
  const [selected, setSelected] = useState<OnlinePlayer | null>(null)
  const [searchMode, setSearchMode] = useState<RadarSearchMode>("RATING")
  const [rank, setRank] = useState<ColorRank>("WHITE")
  const [radarStatus, setRadarStatus] = useState<string>("")

  const playersQ = useQuery({
    queryKey: ["online-players"],
    queryFn: getOnlinePlayers,
    enabled: tab === "online",
    refetchInterval: 20_000,
  })

  useEffect(() => {
    void connectBattleSocket().catch(() => {})
    const unsub = subscribeBattlePresence((s) => setOnlineCount(s.onlineCount))
    const off = onBattleEvent("battle-started", (payload) => {
      navigate("/play/duel", { state: { battle: payload } })
    })
    return () => {
      unsub()
      off()
    }
  }, [navigate])

  async function refreshRadar() {
    try {
      const dto =
        searchMode === "RANK"
          ? buildRadarSearchDto({ searchMode: "RANK", colorRank: rank })
          : buildRadarSearchDto({ searchMode: "RATING" })
      const s = await getRadarStatus(dto ?? undefined)
      setRadarStatus(
        `Очередь ${s.queueSize ?? 0}, матчей ${s.matchableCount ?? 0}`
      )
    } catch {
      setRadarStatus("Не удалось получить статус")
    }
  }

  function startRadar() {
    const dto =
      searchMode === "RANK"
        ? buildRadarSearchDto({ searchMode: "RANK", colorRank: rank })
        : buildRadarSearchDto({ searchMode: "RATING" })
    void emitBattleEventWhenReady(null, "radar-search", dto ?? {})
    void refreshRadar()
  }

  const players = (playersQ.data?.players ?? []).filter(
    (p) => p.userId !== passport?.user_id
  )

  return (
    <div className="px-4 py-6">
      <h1 className="text-xl font-semibold text-guardians-accent">Лобби боя</h1>
      {passport ? (
        <p className="mt-2 text-sm text-guardians-muted">
          ⚡ {passport.energy ?? "—"}/{passport.maxEnergy ?? "—"} · онлайн{" "}
          {onlineCount ?? "…"}
        </p>
      ) : null}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={() => setTab("online")}
          className={clsx(
            "flex-1 rounded-lg py-2 text-sm",
            tab === "online"
              ? "bg-guardians-accent text-guardians-bg"
              : "border border-white/20"
          )}
        >
          Игроки
        </button>
        <button
          type="button"
          onClick={() => {
            setTab("radar")
            void refreshRadar()
          }}
          className={clsx(
            "flex-1 rounded-lg py-2 text-sm",
            tab === "radar"
              ? "bg-guardians-accent text-guardians-bg"
              : "border border-white/20"
          )}
        >
          Радар
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <Link to="/play/matchmaking" className="text-guardians-accent underline">
          Радар / QR
        </Link>
        {DECK_BATTLE_ENABLED ? (
          <Link to="/play/deck-builder" className="text-guardians-accent underline">
            Колодный бой
          </Link>
        ) : null}
        <Link to="/play/arena" className="text-guardians-accent underline">
          Арена
        </Link>
        <Link to="/play/training" className="text-guardians-accent underline">
          Тренировка
        </Link>
        <Link to="/play/hub" className="text-guardians-muted underline">
          Все режимы
        </Link>
      </div>

      {tab === "online" ? (
        <section className="mt-4 space-y-2">
          {playersQ.isLoading ? (
            <p className="text-sm text-guardians-muted">Загрузка…</p>
          ) : (
            players.map((p) => (
              <button
                key={p.userId}
                type="button"
                onClick={() => setSelected(p)}
                className={clsx(
                  "w-full rounded-xl border px-3 py-2 text-left text-sm",
                  selected?.userId === p.userId
                    ? "border-guardians-accent"
                    : "border-white/10"
                )}
              >
                {p.nickname} · {formatPointsValue(p.globalPts)}
              </button>
            ))
          )}
          {selected ? (
            <div className="rounded-xl border border-white/10 p-3 text-sm">
              <p className="font-medium">{selected.nickname}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => sendChallenge(selected, "RATING")}
                  className="rounded-lg bg-guardians-accent px-3 py-1.5 text-xs text-guardians-bg"
                >
                  Вызов
                </button>
                <select
                  className="rounded-lg border border-white/10 bg-transparent px-2 text-xs"
                  onChange={(e) => {
                    const r = e.target.value as ColorRank
                    sendChallenge(selected, "RANK", r)
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>
                    По рангу…
                  </option>
                  {RANKS.map((r) => (
                    <option key={r} value={r}>
                      {RANK_NAMES[r]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <section className="mt-4 space-y-3 text-sm">
          <p className="text-xs text-guardians-muted">
            Энергия за бой: {RADAR_ENERGY_COST}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSearchMode("RATING")}
              className={clsx(
                "flex-1 rounded-lg py-2 text-xs",
                searchMode === "RATING"
                  ? "bg-guardians-accent text-guardians-bg"
                  : "border border-white/20"
              )}
            >
              По рейтингу
            </button>
            <button
              type="button"
              onClick={() => setSearchMode("RANK")}
              className={clsx(
                "flex-1 rounded-lg py-2 text-xs",
                searchMode === "RANK"
                  ? "bg-guardians-accent text-guardians-bg"
                  : "border border-white/20"
              )}
            >
              По рангу
            </button>
          </div>
          {searchMode === "RANK" ? (
            <select
              value={rank}
              onChange={(e) => setRank(e.target.value as ColorRank)}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2"
            >
              {RANKS.map((r) => (
                <option key={r} value={r}>
                  {RANK_NAMES[r]}
                </option>
              ))}
            </select>
          ) : null}
          <p className="text-guardians-muted">{radarStatus || "—"}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={startRadar}
              className="rounded-xl bg-guardians-accent px-4 py-2 text-guardians-bg"
            >
              Искать
            </button>
            <button
              type="button"
              onClick={() => {
                emitBattleEvent("radar-cancel")
                void refreshRadar()
              }}
              className="rounded-xl border border-white/20 px-4 py-2"
            >
              Отмена
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
